import type { GlobalPageCard } from '@confluence/page-card/entry-points/cardTypes.ts';
import { transformGlobalContent } from '@confluence/page-card/entry-points/transformers.ts';

import type { HomeRecentActivitiesQuery_activities_Activities_myActivities_MyActivities_all_ActivitiesConnection_edges_ActivityEdge_node_ActivitiesItem as Recent } from './Recent/__types__/HomeRecentActivitiesQuery.ts';
import type {
	HomeRecentActivityQuery_activity_myActivity_all_edges_node as RecentActivityV3,
	HomeRecentActivityQuery_activity_myActivity_all_edges_node_object_data_ConfluencePage as ConfluencePage,
} from './Recent/__types__/HomeRecentActivityQuery.ts';
import type { HomeRecentCreatedQuery_search_ConfluenceSearchConnection_nodes_ConfluenceSearchResponse as Created } from './Recent/__types__/HomeRecentCreatedQuery.ts';
import type { useDraftsDataHomeDraftsQuery$data } from './Drafts/__generated__/useDraftsDataHomeDraftsQuery.graphql.ts';
import { ActivityEventType, isCreatorCurrentUser } from './homeViewsQueryHelpers.ts';

type Draft = NonNullable<
	NonNullable<NonNullable<useDraftsDataHomeDraftsQuery$data['search']>['nodes']>[number]
>;

const getLastActionFromRecent = (
	recent: Recent | RecentActivityV3,
	isActivityV3HomeRecent: boolean,
) => {
	const timestamp = isActivityV3HomeRecent
		? (recent as RecentActivityV3).event?.timestamp || ''
		: (recent as Recent).timestamp || '';
	const recentAction = isActivityV3HomeRecent
		? (recent as RecentActivityV3).event?.eventType
		: (recent as Recent).object?.events?.[0].eventType;
	let action: GlobalPageCard['lastAction']['action'];
	switch (recentAction) {
		case ActivityEventType.COMMENTED:
			action = 'commented';
			break;
		case ActivityEventType.EDITED:
		case ActivityEventType.UPDATED:
		case ActivityEventType.PUBLISHED:
		case ActivityEventType.SNAPSHOTTED:
			action = 'edited';
			break;
		case ActivityEventType.CREATED:
		case ActivityEventType.STARTED:
			action = 'createdByYou';
			break;
		case ActivityEventType.VIEWED:
			action = 'visited';
			break;
		case ActivityEventType.LIKED:
			action = 'liked';
			break;
	}
	return {
		when: timestamp,
		action,
	};
};

export const transformRecentToPageCard = (
	recent: Recent | RecentActivityV3,
	untitledDraftText = '',
	isWhiteboardEmojiFGEnabled = false,
	isActivityV3HomeRecent = false,
): GlobalPageCard => {
	const content = isActivityV3HomeRecent
		? (recent as RecentActivityV3).object?.data
		: (recent as Recent).object?.content;

	return {
		...transformGlobalContent(
			content,
			untitledDraftText,
			isWhiteboardEmojiFGEnabled,
			isActivityV3HomeRecent,
		),

		// Using ConfluencePage to fix type error caused by ConfluenceFooterComment not having id in the types file
		key: isActivityV3HomeRecent
			? `${(content as ConfluencePage)?.id}-${(recent as RecentActivityV3)?.event?.eventType}`
			: `${(content as ConfluencePage)?.id}-${(recent as Recent).object?.events?.[0].eventType}`,
		lastAction: getLastActionFromRecent(recent, isActivityV3HomeRecent),
	};
};

export const transformCreatedByMeToPageCard = (
	created: Created,
	untitledDraftText = '',
	userId: string | null = null,
): GlobalPageCard => {
	const id = created?.content?.id || '';
	const content = created?.content;

	const isContentCreatedByCurrentUser = isCreatorCurrentUser(userId, content);
	const actionText = !isContentCreatedByCurrentUser ? 'ownedByYou' : 'createdByYou';

	return {
		...transformGlobalContent(content, untitledDraftText),
		key: `${id}-created`,
		lastAction: {
			when: created.content?.metadata.createdDate || '',
			action: actionText,
		},
	};
};

export const transformDraftToPageCard = (draft: Draft, untitledDraftText = ''): GlobalPageCard => {
	const content = draft?.content;
	const id = content?.id || '';

	return {
		...transformGlobalContent(content, untitledDraftText),
		key: `${id}-draft`,
		lastAction: {
			when: content?.metadata.lastModifiedDate || '',
			action: 'edited',
		},
	};
};

export const transformStarredToPageCard = (data: any, untitledDraftText = ''): GlobalPageCard => {
	return {
		...transformGlobalContent(data, untitledDraftText),
		lastAction: {
			when: data?.metadata.currentuser?.viewed?.lastSeen || data?.history?.lastUpdated?.when || '',
			action: data?.metadata.currentuser?.viewed?.lastSeen ? 'visited' : 'updated',
		},
	};
};
